var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { useCreatePath, useGetResourceLabel, useResourceDefinitions, useTranslate, useBasename, } from 'react-admin';
/**
 * Internal hook that builds a map of paths from a resource definition
 *
 * The output map has the following form:
 * {
 *  songs: { label: 'Songs', to: '/songs' },
 *  songs.create: { label: 'Create Song', to: '/songs/create' },
 *  songs.show: {
 *    label: ({ record }) => `Show #${record.id}`,
 *    to: ({ record }) => `/${record.id}/show`
 *  }
 *  songs.edit: {
 *    label: ({ record }) => `Edit #${record.id}`,
 *    to: ({ record }) => `/${record.id}/edit`
 *  }
 * }
 */
export var useBuildResourceBreadcrumbPaths = function () {
    var getResourceLabel = useGetResourceLabel();
    var translate = useTranslate();
    var createPath = useCreatePath();
    var basename = useBasename();
    return function (resource) {
        var resourcePaths = {};
        var resourceLabelPlural = getResourceLabel(resource.name, 2);
        var resourceLabelSingular = getResourceLabel(resource.name, 1);
        resourcePaths[resource.name] = {
            label: resourceLabelPlural,
            to: "".concat(basename, "/").concat(resource.name),
        };
        resourcePaths["".concat(resource.name, ".create")] = {
            label: !resource.hasList
                ? translate('ra.page.create', {
                    name: resourceLabelSingular,
                })
                : translate('ra.action.create'),
            to: createPath({
                resource: resource.name,
                type: 'create',
            }),
        };
        resourcePaths["".concat(resource.name, ".edit")] = {
            label: function (_a) {
                var record = _a.record;
                return !record
                    ? translate('ra.action.edit')
                    : !resource.hasList
                        ? translate('ra.page.edit', {
                            name: resourceLabelSingular,
                            id: record.id,
                            record: record,
                        })
                        : "#".concat(record.id);
            },
            to: function (_a) {
                var record = _a.record;
                return record &&
                    createPath({
                        resource: resource.name,
                        id: record.id,
                        type: 'edit',
                    });
            },
        };
        resourcePaths["".concat(resource.name, ".show")] = {
            label: function (_a) {
                var record = _a.record;
                return !record
                    ? translate('ra.action.show')
                    : !resource.hasList
                        ? translate('ra.page.show', {
                            name: resourceLabelSingular,
                            id: record.id,
                            record: record,
                        })
                        : "#".concat(record.id);
            },
            to: function (_a) {
                var record = _a.record;
                return record &&
                    createPath({
                        resource: resource.name,
                        id: record.id,
                        type: 'show',
                    });
            },
        };
        return resourcePaths;
    };
};
/**
 * This hook is used internally to build a resource breadcrumb path map
 * The result is usually used by <ResourceBreadcrumbItems /> to render a BreadcrumbItem tree from current resources
 *
 * @see ResourceBreadcrumbItems
 */
export var useResourcesBreadcrumbPaths = function (selectedResources) {
    var resources = useResourceDefinitions();
    var buildResourceBreadcrumbPaths = useBuildResourceBreadcrumbPaths();
    return Object.values(resources)
        .filter(function (resource) {
        return !selectedResources || selectedResources.includes(resource.name);
    })
        .map(function (resource) { return (__assign(__assign({}, resource), { hasCreate: !!resource.hasCreate, hasEdit: !!resource.hasEdit, hasList: !!resource.hasList, hasShow: !!resource.hasShow })); })
        .reduce(function (resourcesPaths, resource) { return (__assign(__assign({}, resourcesPaths), buildResourceBreadcrumbPaths(resource))); }, {});
};
